/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import { Component, Type } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import {
    IBaseRequestPromise,
    MessageItem,
    ObjectTypeItem,
} from 'ajs/modules/data-model/factories';

import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import {
    AviPermissionResource,
    FileType,
    ICRL,
    IFileObject,
} from 'generated-types';

import { FileObject } from 'object-types';
import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { IHttpResponse, IPromise } from 'angular';
import * as l10n from './file-object.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Extended config interface with message item types.
 */
interface IFileObjectConfig extends Omit<IFileObject, 'crl_info'> {
    crl_info?: MessageItem<ICRL>
}

/**
 * API endpoints. Used for FileObject creation.
 */
const FILE_UPLOAD_URL = '/api/fileobject/upload';
const ADD_CRL_BY_SERVER_URL = 'api/fileobject';

/**
 * @description FileObject Item class.
 * @author alextsg
 */
export class FileObjectItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public static ngDependencies = [L10nService];

    public getConfig: () => IFileObjectConfig;

    public l10nService: L10nService;

    public fileContent: string;

    public crlUpdateInterval: number;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: FileObject,
            objectName: 'fileobject',
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_FILE_OBJECT,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getNgDependency(L10nService);
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Return file type.
     */
    public getType(): FileType {
        return this.getConfig().type;
    }

    /**
     * Set the type of FileObject.
     */
    public setType(type: FileType): void {
        this.getConfig().type = type;
    }

    /**
     * Return the filename from the file path.
     */
    public getFileName(): string {
        const { path = '' } = this.getConfig();
        const pathParts = path.split('/');

        return pathParts[pathParts.length - 1] || '';
    }

    /**
     * Method used to import lazy-loaded modal component.
     */
    /* eslint-disable-next-line class-methods-use-this */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { FileObjectModalComponent } = await import(
            /* webpackChunkName: "file-object-modal" */
            'ng/lazy-loaded-components/modals/file-object-modal/file-object-modal.component'
        );

        return FileObjectModalComponent as Type<Component>;
    }

    /**
     * @override
     * FileObjects with a parent are not editable.
     */
    public isEditable(): boolean {
        return super.isEditable() && !this.isChild();
    }

    /**
     * @override
     * FileObjects with a parent are not droppable.
     */
    public isDroppable(): boolean {
        return super.isDroppable() && !this.isChild();
    }

    /**
     * @override
     * Save FileObject. FileObjects are not created through typical POST/PUT on api/fileobject. To
     * create and update a FileObject, we must use api/fileobject/upload to upload a new FileObject,
     * or in the case of CRL type FileObjects, we can use api/fileobject/crlupload to specify a
     * server_url.
     * TODO: refactor to use POST/PUT workflow after support is added by backend (AV-217286)
     */
    protected saveRequest(): IPromise<IHttpResponse<IFileObject>> {
        // User is uploading new CRL file.
        if (this.fileContent) {
            return this.uploadSaveRequest();
        }

        const { type, crl_info: crlInfo } = this.getConfig();

        // User is creating/modifying a CRL FileObject using a server URL and fields have been
        // modified.
        if (type === FileType.CRL_DATA && crlInfo.config.server_url && this.modified()) {
            return this.addCrlByServerUrlSaveRequest();
        }

        // Else save the FileObject as usual.
        return super.saveRequest();
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.fileObjectModalBreadcrumbTitle);
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'crl_info',
        ];
    }

    /**
     * Create or update CRL type FileObject by server URL.
     */
    private addCrlByServerUrlSaveRequest(): IBaseRequestPromise<any> {
        const {
            name,
            description,
            is_federated: isFederated = false,
            crl_info: crlInfo,
        } = this.getConfig();

        const {
            update_interval: updateInterval,
            server_url: serverUrl,
        } = crlInfo.config;

        const payload = {
            name,
            description,
            is_federated: isFederated,
            update_interval: updateInterval,
            url: serverUrl,
        };

        return this.request('POST', ADD_CRL_BY_SERVER_URL, payload);
    }

    /**
     * Create or update FileObject by file upload.
     */
    private uploadSaveRequest(): IBaseRequestPromise<IFileObject> {
        const formData = new FormData();
        const {
            name,
            description,
            type,
            gslb_geodb_format: gslbGeodbFormat,
            is_federated: isFederated = false,
        } = this.getConfig();

        formData.append('file', this.fileContent);
        formData.append('name', name);
        formData.append('type', type);

        // Optional field.
        if (description) {
            formData.append('description', description);
        }

        if (type === FileType.GSLB_GEO_DB) {
            formData.append('gslb_geo_format', gslbGeodbFormat);
        }

        // Form Data doesn't support appending boolean values directly. Thus, need to convert this
        // to string.
        formData.append('is_federated', isFederated.toString());

        return this.request('POST', FILE_UPLOAD_URL, formData, { 'Content-Type': undefined });
    }

    /**
     * Return true if the FileObject has a parent.
     */
    private isChild(): boolean {
        return Boolean(this.getConfig().has_parent);
    }
}
